import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import api from '../api';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';
import {
  AlertTriangle, CheckCircle, Clock, RefreshCw, Shield,
  TrendingDown, TrendingUp, Info, ChevronDown, ChevronUp,
  RotateCcw, ThumbsUp, Calendar, Filter, Plus, X, Loader2,
  Flame, GitCommit, FileWarning, UserCheck, FileText, Bell, Zap,
  PenLine
} from 'lucide-react';
import AdminSecurityHelpButton from '../components/Admin/AdminSecurityHelpButton';

// ─── Types ──────────────────────────────────────────────────────────────────

interface DriftEvent {
  id: number;
  event_type: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  status: 'open' | 'approved' | 'reverted' | 'resolved' | 'ignored';
  title: string;
  description: string;
  impact_description: string;
  affected_standards: string[];
  compliance_score_before: number | null;
  compliance_score_after: number | null;
  resource_id: string | null;
  resource_type: string | null;
  changed_by: string | null;
  change_details: Record<string, any>;
  detected_at: string;
  resolved_at: string | null;
  resolution_notes: string | null;
  source: string;
}

interface ComplianceScore {
  current_score: number;
  yesterday_score: number;
  score_change: number;
  new_issues_24h: number;
  open_critical: number;
  open_high: number;
  open_medium: number;
}

interface ScoreHistoryPoint {
  date: string;
  score: number;
  label: string;
}

// ─── Add Event Modal ──────────────────────────────────────────────────────────

const EVENT_TYPES = [
  { value: 'firewall_change', label: 'Firewall Change' },
  { value: 'config_change', label: 'Config Change' },
  { value: 'policy_violation', label: 'Policy Violation' },
  { value: 'code_commit_no_review', label: 'Code Commit Without Review' },
  { value: 'evidence_stale', label: 'Evidence Stale' },
  { value: 'mfa_change', label: 'MFA Change' },
  { value: 'user_access_change', label: 'User Access Change' },
  { value: 'security_policy_update', label: 'Security Policy Update' },
  { value: 'certificate_change', label: 'Certificate Change' },
  { value: 'infrastructure_change', label: 'Infrastructure Change' },
  { value: 'compliance_improvement', label: 'Compliance Improvement' },
  { value: 'custom', label: 'Custom / Other' },
];

const COMMON_STANDARDS = ['OWASP Top 10', 'ISO 27001', 'PCI DSS', 'SOC 2', 'NIST CSF', 'GDPR', 'HIPAA', 'CIS Controls'];

const AddEventModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ isOpen, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    event_type: 'custom',
    severity: 'medium',
    title: '',
    description: '',
    impact_description: '',
    resource_id: '',
    resource_type: '',
    changed_by: '',
    affected_standards: [] as string[],
    custom_standard: '',
  });
  const [saving, setSaving] = useState(false);

  const set = (field: string, val: any) => setForm(f => ({ ...f, [field]: val }));

  const toggleStandard = (std: string) => {
    setForm(f => ({
      ...f,
      affected_standards: f.affected_standards.includes(std)
        ? f.affected_standards.filter(s => s !== std)
        : [...f.affected_standards, std]
    }));
  };

  const addCustomStandard = () => {
    const s = form.custom_standard.trim();
    if (s && !form.affected_standards.includes(s)) {
      setForm(f => ({ ...f, affected_standards: [...f.affected_standards, s], custom_standard: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await api.post('/api/compliance-drift/events', {
        event_type: form.event_type,
        severity: form.severity,
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        impact_description: form.impact_description.trim() || undefined,
        resource_id: form.resource_id.trim() || undefined,
        resource_type: form.resource_type.trim() || undefined,
        changed_by: form.changed_by.trim() || undefined,
        affected_standards: form.affected_standards,
        source: 'manual',
      });
      toast.success('Drift event logged');
      onSuccess();
      onClose();
      setForm({
        event_type: 'custom', severity: 'medium', title: '', description: '',
        impact_description: '', resource_id: '', resource_type: '',
        changed_by: '', affected_standards: [], custom_standard: '',
      });
    } catch {
      toast.error('Failed to log event');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <PenLine className="w-5 h-5 text-indigo-500" />
            Log Manual Drift Event
          </h2>
          <button onClick={onClose} className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={form.title}
              onChange={e => set('title', e.target.value)}
              placeholder="e.g. Firewall rule modified on prod server"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              required
            />
          </div>

          {/* Event type + Severity */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
              <select
                value={form.event_type}
                onChange={e => set('event_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              >
                {EVENT_TYPES.map(et => (
                  <option key={et.value} value={et.value}>{et.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
              <select
                value={form.severity}
                onChange={e => set('severity', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              >
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low / Improvement</option>
                <option value="info">Info</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={e => set('description', e.target.value)}
              rows={2}
              placeholder="What happened?"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
            />
          </div>

          {/* Impact */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Impact Description</label>
            <input
              type="text"
              value={form.impact_description}
              onChange={e => set('impact_description', e.target.value)}
              placeholder="e.g. PCI DSS 1.2.1 compliance dropped"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>

          {/* Resource + Changed by */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Resource ID</label>
              <input
                type="text"
                value={form.resource_id}
                onChange={e => set('resource_id', e.target.value)}
                placeholder="e.g. AWS sg-abc123"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Changed By</label>
              <input
                type="text"
                value={form.changed_by}
                onChange={e => set('changed_by', e.target.value)}
                placeholder="e.g. dev@company.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
          </div>

          {/* Affected Standards */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Affected Standards</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {COMMON_STANDARDS.map(std => (
                <button
                  key={std}
                  type="button"
                  onClick={() => toggleStandard(std)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                    form.affected_standards.includes(std)
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'
                  }`}
                >
                  {std}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={form.custom_standard}
                onChange={e => set('custom_standard', e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomStandard(); }}}
                placeholder="Add custom standard..."
                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
              <button
                type="button"
                onClick={addCustomStandard}
                className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg border border-gray-300"
              >
                Add
              </button>
            </div>
            {form.affected_standards.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {form.affected_standards.map(std => (
                  <span key={std} className="flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded text-xs">
                    {std}
                    <button type="button" onClick={() => toggleStandard(std)} className="hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !form.title.trim()}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}
              Log Event
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Config maps ─────────────────────────────────────────────────────────────

const SEVERITY_CONFIG = {
  critical: { label: 'CRITICAL', bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', dot: 'bg-red-500', icon: '🔴' },
  high:     { label: 'HIGH RISK', bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', dot: 'bg-orange-500', icon: '🔴' },
  medium:   { label: 'MEDIUM RISK', bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300', dot: 'bg-yellow-500', icon: '⚠️' },
  low:      { label: 'IMPROVEMENT', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300', dot: 'bg-green-500', icon: '✅' },
  info:     { label: 'INFO', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', dot: 'bg-blue-500', icon: 'ℹ️' },
};

const EVENT_TYPE_ICONS: Record<string, React.ReactNode> = {
  firewall_change:         <Flame className="w-5 h-5 text-red-500" />,
  code_commit_no_review:   <GitCommit className="w-5 h-5 text-orange-500" />,
  evidence_stale:          <FileWarning className="w-5 h-5 text-yellow-500" />,
  mfa_change:              <UserCheck className="w-5 h-5 text-green-500" />,
  security_policy_update:  <FileText className="w-5 h-5 text-blue-500" />,
  config_change:           <Shield className="w-5 h-5 text-purple-500" />,
  certificate_change:      <Shield className="w-5 h-5 text-indigo-500" />,
  infrastructure_change:   <Flame className="w-5 h-5 text-orange-500" />,
  compliance_improvement:  <CheckCircle className="w-5 h-5 text-green-500" />,
  policy_violation:        <AlertTriangle className="w-5 h-5 text-red-500" />,
  user_access_change:      <UserCheck className="w-5 h-5 text-blue-500" />,
  custom:                  <Info className="w-5 h-5 text-gray-500" />,
};

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = now.getTime() - date.getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 60) return `${mins} minute${mins !== 1 ? 's' : ''} ago`;
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

// ─── Custom Tooltip for chart ─────────────────────────────────────────────────

const ScoreTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const score = payload[0].value;
    const color = score >= 80 ? '#16a34a' : score >= 60 ? '#ca8a04' : '#dc2626';
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2">
        <p className="text-xs text-gray-500 mb-1">{label}</p>
        <p className="text-lg font-bold" style={{ color }}>{score}%</p>
      </div>
    );
  }
  return null;
};

// ─── Event Card ───────────────────────────────────────────────────────────────

const EventCard: React.FC<{
  event: DriftEvent;
  onStatusChange: (id: number, status: string, notes?: string) => void;
  updating: boolean;
}> = ({ event, onStatusChange, updating }) => {
  const [expanded, setExpanded] = useState(false);
  const cfg = SEVERITY_CONFIG[event.severity] || SEVERITY_CONFIG.info;
  const icon = EVENT_TYPE_ICONS[event.event_type] || <Info className="w-5 h-5 text-gray-500" />;
  const isOpen = event.status === 'open';

  return (
    <div className={`border rounded-xl overflow-hidden transition-shadow hover:shadow-md ${cfg.border} bg-white`}>
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${cfg.bg}`}>
              {icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h3 className="font-semibold text-gray-900 text-sm">{event.title}</h3>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
                  {cfg.icon} {cfg.label}
                </span>
                {event.status !== 'open' && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 capitalize">
                    {event.status}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Clock className="w-3 h-3" />
                <span>{timeAgo(event.detected_at)}</span>
                {event.changed_by && (
                  <>
                    <span>•</span>
                    <span>by {event.changed_by}</span>
                  </>
                )}
                {event.source && event.source !== 'manual' && (
                  <>
                    <span>•</span>
                    <span className="capitalize">{event.source}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={() => setExpanded(v => !v)}
            className="flex-shrink-0 p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>

        {/* Impact */}
        {event.impact_description && (
          <div className={`mt-3 px-3 py-2 rounded-lg text-sm ${cfg.bg} ${cfg.text}`}>
            <span className="font-medium">Impact: </span>{event.impact_description}
          </div>
        )}

        {/* Affected standards — always visible */}
        {event.affected_standards?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {event.affected_standards.map(std => (
              <span key={std} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded text-xs font-medium">{std}</span>
            ))}
          </div>
        )}
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className={`px-4 pb-4 border-t ${cfg.border} bg-gray-50 space-y-3`}>
          {event.description && (
            <p className="text-sm text-gray-600 pt-3">{event.description}</p>
          )}

          {/* Change details */}
          {event.change_details && Object.keys(event.change_details).length > 0 && (
            <div className="space-y-1">
              {Object.entries(event.change_details).map(([key, val]) => (
                <div key={key} className="flex gap-2 text-xs">
                  <span className="text-gray-500 capitalize min-w-16">{key.replace(/_/g, ' ')}:</span>
                  <span className="text-gray-800 font-mono">{String(val)}</span>
                </div>
              ))}
            </div>
          )}

          {event.resource_id && (
            <div className="text-xs text-gray-500">
              <span className="font-medium">Resource: </span>
              <code className="bg-gray-100 px-1 py-0.5 rounded">{event.resource_id}</code>
            </div>
          )}

          {/* Compliance score change */}
          {event.compliance_score_before != null && event.compliance_score_after != null && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-600">Compliance:</span>
              <span className="font-bold text-gray-800">{event.compliance_score_before}%</span>
              <span className="text-gray-400">→</span>
              <span className={`font-bold ${event.compliance_score_after < event.compliance_score_before ? 'text-red-600' : 'text-green-600'}`}>
                {event.compliance_score_after}%
              </span>
            </div>
          )}

          {/* Action buttons */}
          {isOpen && (
            <div className="flex flex-wrap gap-2 pt-1">
              <button
                disabled={updating}
                onClick={() => onStatusChange(event.id, 'reverted')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {updating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                Revert Change
              </button>
              <button
                disabled={updating}
                onClick={() => onStatusChange(event.id, 'approved')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {updating ? <Loader2 className="w-3 h-3 animate-spin" /> : <ThumbsUp className="w-3 h-3" />}
                Mark as Approved
              </button>
              {event.event_type === 'evidence_stale' && (
                <button
                  disabled={updating}
                  onClick={() => onStatusChange(event.id, 'resolved', 'New test scheduled')}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  <Calendar className="w-3 h-3" />
                  Schedule New Test
                </button>
              )}
              <button
                disabled={updating}
                onClick={() => onStatusChange(event.id, 'ignored')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
              >
                <X className="w-3 h-3" />
                Ignore
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const ComplianceDrift: React.FC = () => {
  const [events, setEvents] = useState<DriftEvent[]>([]);
  const [score, setScore] = useState<ComplianceScore | null>(null);
  const [scoreHistory, setScoreHistory] = useState<ScoreHistoryPoint[]>([]);
  const [hasSampleData, setHasSampleData] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('open');
  const [isSeeding, setIsSeeding] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isRunningDetection, setIsRunningDetection] = useState(false);
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [isAddEventOpen, setIsAddEventOpen] = useState(false);

  // Track known critical event IDs for alert deduplication
  const knownCriticalIds = useRef<Set<number>>(new Set());
  const isFirstLoad = useRef(true);

  const checkAndFireAlerts = useCallback((newEvents: DriftEvent[]) => {
    if (!alertsEnabled) return;
    // Skip alerting on first load (don't spam existing events)
    if (isFirstLoad.current) {
      newEvents
        .filter(e => e.status === 'open' && (e.severity === 'critical' || e.severity === 'high'))
        .forEach(e => knownCriticalIds.current.add(e.id));
      isFirstLoad.current = false;
      return;
    }

    const newCritical = newEvents.filter(
      e => e.status === 'open' &&
           (e.severity === 'critical' || e.severity === 'high') &&
           !knownCriticalIds.current.has(e.id)
    );

    newCritical.forEach(e => {
      knownCriticalIds.current.add(e.id);
      toast.error(
        `🚨 New ${e.severity.toUpperCase()} drift detected: ${e.title}`,
        {
          duration: 8000,
          icon: e.severity === 'critical' ? '🔴' : '🟠',
          style: {
            background: e.severity === 'critical' ? '#fef2f2' : '#fff7ed',
            border: `1px solid ${e.severity === 'critical' ? '#fecaca' : '#fed7aa'}`,
            color: '#1f2937',
          }
        }
      );
    });
  }, [alertsEnabled]);

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    else setIsRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (severityFilter) params.append('severity', severityFilter);
      if (statusFilter) params.append('status', statusFilter);

      const [eventsRes, scoreRes, allEventsRes, historyRes] = await Promise.all([
        api.get(`/api/compliance-drift/events?${params.toString()}`),
        api.get('/api/compliance-drift/score'),
        api.get('/api/compliance-drift/events?limit=200'),
        api.get('/api/compliance-drift/score-history?days=7')
      ]);

      const allEvents: DriftEvent[] = allEventsRes.data.data || [];
      const fetchedEvents: DriftEvent[] = eventsRes.data.data || [];

      setEvents(fetchedEvents);
      setScore(scoreRes.data.data);
      setScoreHistory(historyRes.data.data || []);
      setHasSampleData(allEvents.some(e => ['sample', 'aws', 'github', 'system'].includes(e.source)));

      // Fire alerts for new critical/high events
      checkAndFireAlerts(allEvents);
    } catch (err: any) {
      if (!silent) toast.error('Failed to load compliance drift data');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [severityFilter, statusFilter, checkAndFireAlerts]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh every 60 seconds to pick up new drift events
  useEffect(() => {
    const interval = setInterval(() => {
      loadData(true);
    }, 60000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleStatusChange = async (id: number, status: string, notes?: string) => {
    setUpdatingId(id);
    try {
      await api.patch(`/api/compliance-drift/events/${id}/status`, {
        status,
        resolution_notes: notes
      });
      // Remove from known criticals if resolved/approved/ignored
      if (['resolved', 'approved', 'reverted', 'ignored'].includes(status)) {
        knownCriticalIds.current.delete(id);
      }
      toast.success(`Event marked as ${status}`);
      await loadData(true);
    } catch {
      toast.error('Failed to update event status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleSeedData = async () => {
    setIsSeeding(true);
    try {
      const res = await api.post('/api/compliance-drift/seed', {});
      toast.success(res.data.message);
      // Reset known IDs so new sample data alerts fire properly on next refresh
      knownCriticalIds.current.clear();
      isFirstLoad.current = true;
      await loadData(true);
    } catch {
      toast.error('Failed to seed sample data');
    } finally {
      setIsSeeding(false);
    }
  };

  const handleRunDetection = async () => {
    setIsRunningDetection(true);
    try {
      const res = await api.post('/api/compliance-drift/run-detection', {});
      const { total_created, total_skipped, sources_checked, message } = res.data;
      if (total_created > 0) {
        toast.success(`${message}`, { duration: 5000 });
        // Reset so new events trigger alerts
        isFirstLoad.current = false;
        await loadData(true);
      } else {
        toast.success(
          sources_checked === 0
            ? 'No active sources found. Add a GitHub or AWS source first.'
            : `No new drift detected across ${sources_checked} source(s) (${total_skipped} already known)`,
          { duration: 4000 }
        );
      }
    } catch {
      toast.error('Failed to run drift detection');
    } finally {
      setIsRunningDetection(false);
    }
  };

  const handleClearSample = async () => {
    setIsClearing(true);
    try {
      await api.delete('/api/compliance-drift/clear-sample');
      toast.success('Sample data removed');
      knownCriticalIds.current.clear();
      isFirstLoad.current = true;
      await loadData(true);
    } catch {
      toast.error('Failed to remove sample data');
    } finally {
      setIsClearing(false);
    }
  };

  const scoreColor = score
    ? score.current_score >= 80 ? 'text-green-600'
      : score.current_score >= 60 ? 'text-yellow-600'
      : 'text-red-600'
    : 'text-gray-400';

  const scoreBg = score
    ? score.current_score >= 80 ? 'bg-green-50 border-green-200'
      : score.current_score >= 60 ? 'bg-yellow-50 border-yellow-200'
      : 'bg-red-50 border-red-200'
    : 'bg-gray-50 border-gray-200';

  const chartColor = score
    ? score.current_score >= 80 ? '#16a34a'
      : score.current_score >= 60 ? '#ca8a04'
      : '#dc2626'
    : '#6366f1';

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              Real-Time Compliance Drift
            </h1>
            <p className="text-gray-600">Monitor compliance changes and infrastructure modifications in real-time</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <AdminSecurityHelpButton screenId="compliance-drift" />
            {/* Alert toggle */}
            <button
              onClick={() => {
                setAlertsEnabled(v => !v);
                toast.success(alertsEnabled ? 'Alerts disabled' : 'Alerts enabled');
              }}
              title={alertsEnabled ? 'Disable alerts' : 'Enable alerts'}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                alertsEnabled
                  ? 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'
                  : 'bg-gray-100 border-gray-200 text-gray-500 hover:bg-gray-200'
              }`}
            >
              <Bell className={`w-4 h-4 ${alertsEnabled ? 'animate-pulse' : ''}`} />
              {alertsEnabled ? 'Alerts ON' : 'Alerts OFF'}
            </button>
            <button
              onClick={() => setIsAddEventOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
            >
              <PenLine className="w-4 h-4" />
              Log Event
            </button>
            <button
              onClick={handleRunDetection}
              disabled={isRunningDetection}
              title="Run drift detection on all active evidence sources"
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 transition-colors"
            >
              {isRunningDetection ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              Run Detection
            </button>
            <button
              onClick={() => loadData(true)}
              disabled={isRefreshing}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {!isLoading && (hasSampleData ? (
              <button
                onClick={handleClearSample}
                disabled={isClearing}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
              >
                {isClearing ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                Hide Sample Data
              </button>
            ) : (
              <button
                onClick={handleSeedData}
                disabled={isSeeding}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#221551] hover:bg-[#454F83] rounded-lg transition-colors disabled:opacity-50"
              >
                {isSeeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Load Sample Data
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Score cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {/* Current Score */}
        <div className={`rounded-xl border p-6 ${scoreBg}`}>
          <p className="text-sm font-medium text-gray-600 mb-1">Current Compliance Score</p>
          <p className={`text-5xl font-bold ${scoreColor}`}>
            {score ? `${score.current_score}%` : '—'}
          </p>
          {score && (
            <div className={`flex items-center gap-1 mt-2 text-sm font-medium ${score.score_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {score.score_change >= 0
                ? <TrendingUp className="w-4 h-4" />
                : <TrendingDown className="w-4 h-4" />
              }
              {score.score_change >= 0 ? '+' : ''}{score.score_change}% from yesterday
            </div>
          )}
        </div>

        {/* New issues */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <p className="text-sm font-medium text-gray-600 mb-1">New Issues (24h)</p>
          <p className="text-5xl font-bold text-orange-600">{score?.new_issues_24h ?? '—'}</p>
          <p className="text-xs text-gray-500 mt-2">Detected in last 24 hours</p>
        </div>

        {/* Open critical */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <p className="text-sm font-medium text-gray-600 mb-1">Open Critical / High</p>
          <p className="text-5xl font-bold text-red-600">
            {score ? score.open_critical + score.open_high : '—'}
          </p>
          <p className="text-xs text-gray-500 mt-2">Require immediate attention</p>
        </div>

        {/* Open medium */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <p className="text-sm font-medium text-gray-600 mb-1">Open Medium</p>
          <p className="text-5xl font-bold text-yellow-600">{score?.open_medium ?? '—'}</p>
          <p className="text-xs text-gray-500 mt-2">Monitor and resolve soon</p>
        </div>
      </div>

      {/* Score Over Time Chart */}
      {scoreHistory.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-indigo-500" />
            Compliance Score — Last 7 Days
          </h2>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={scoreHistory} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: '#6b7280' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: 11, fill: '#6b7280' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip content={<ScoreTooltip />} />
              {/* Reference lines for thresholds */}
              <ReferenceLine y={80} stroke="#16a34a" strokeDasharray="4 4" strokeOpacity={0.4} label={{ value: 'Good', position: 'right', fontSize: 10, fill: '#16a34a' }} />
              <ReferenceLine y={60} stroke="#ca8a04" strokeDasharray="4 4" strokeOpacity={0.4} label={{ value: 'Warning', position: 'right', fontSize: 10, fill: '#ca8a04' }} />
              <Line
                type="monotone"
                dataKey="score"
                stroke={chartColor}
                strokeWidth={2.5}
                dot={{ r: 4, fill: chartColor, strokeWidth: 0 }}
                activeDot={{ r: 6, fill: chartColor }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <Filter className="w-4 h-4" />
          Filter:
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
        >
          <option value="">All Statuses</option>
          <option value="open">Open</option>
          <option value="approved">Approved</option>
          <option value="reverted">Reverted</option>
          <option value="resolved">Resolved</option>
          <option value="ignored">Ignored</option>
        </select>
        <select
          value={severityFilter}
          onChange={e => setSeverityFilter(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
        >
          <option value="">All Severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low / Improvement</option>
          <option value="info">Info</option>
        </select>
        {(statusFilter || severityFilter) && (
          <button
            onClick={() => { setSeverityFilter(''); setStatusFilter(''); }}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <X className="w-3 h-3" /> Clear filters
          </button>
        )}
        <span className="ml-auto text-sm text-gray-500">{events.length} event{events.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Timeline */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Clock className="w-5 h-5 text-[#57C8F4]" />
          Compliance Timeline
        </h2>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#57C8F4]" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-gray-200">
            <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No compliance drift events found</p>
            <p className="text-sm text-gray-400 mt-1">
              {statusFilter || severityFilter
                ? 'Try clearing the filters'
                : 'Click "Load Sample Data" to see a demo'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {events.map(event => (
              <EventCard
                key={event.id}
                event={event}
                onStatusChange={handleStatusChange}
                updating={updatingId === event.id}
              />
            ))}
          </div>
        )}
      </div>
      {/* Add Manual Event Modal */}
      <AddEventModal
        isOpen={isAddEventOpen}
        onClose={() => setIsAddEventOpen(false)}
        onSuccess={() => loadData(true)}
      />
    </div>
  );
};

export default ComplianceDrift;
